import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { Layout } from "../components/Layout";
import { LotusMark } from "../components/Logo";
import { siteConfig } from "../data/siteConfig";

export const ContactPage = () => {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Client-side only for now — wire up to an email service / backend later.
    setSent(true);
  };

  const contactCards = [
    {
      icon: MapPin,
      label: "Headquarters",
      value: siteConfig.headquarters,
      href: null,
    },
    {
      icon: Phone,
      label: "Phone",
      value: siteConfig.phoneDisplay,
      href: `tel:+${siteConfig.phoneE164}`,
    },
    {
      icon: Mail,
      label: "Email",
      value: siteConfig.email,
      href: `mailto:${siteConfig.email}`,
    },
  ];

  return (
    <Layout>
      <section className="pt-28 md:pt-32 pb-12">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
            <LotusMark className="w-4 h-4" /> Contact
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal mb-4">
            Let's connect
          </h1>
          <p className="text-charcoal/70 text-lg">
            Questions about a program, scheduling, or booking a class? Send a
            message and Suman will get back to you.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-10">
          {/* Contact details */}
          <div>
            <div className="grid sm:grid-cols-2 gap-5">
              {contactCards.map((c) => {
                const inner = (
                  <>
                    <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                      <c.icon className="w-5 h-5" />
                    </span>
                    <p className="text-xs uppercase tracking-widest text-charcoal/50 mb-1">
                      {c.label}
                    </p>
                    <p className="font-medium text-charcoal break-words">
                      {c.value}
                    </p>
                  </>
                );
                return c.href ? (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="bg-white rounded-2xl p-6 border border-accent/15 shadow-soft hover:-translate-y-1 transition-transform"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={c.label}
                    className="bg-white rounded-2xl p-6 border border-accent/15 shadow-soft"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>

            <a
              href={siteConfig.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-2xl py-4 font-semibold hover:opacity-90 transition-opacity"
            >
              <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
            </a>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 border border-accent/15 shadow-soft">
            <h2 className="font-heading text-2xl text-charcoal mb-6">
              Send a message
            </h2>
            {sent ? (
              <div className="text-center py-10">
                <LotusMark className="w-12 h-12 mx-auto mb-4" />
                <p className="font-heading text-2xl text-charcoal mb-2">
                  Thank you!
                </p>
                <p className="text-charcoal/70">
                  Your message has been noted. Suman will be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    required
                    className="contact-input w-full px-4 py-3"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    className="contact-input w-full px-4 py-3"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1.5">
                    Program of interest
                  </label>
                  <input
                    type="text"
                    className="contact-input w-full px-4 py-3"
                    placeholder="e.g. Prenatal, Kids Yoga, 1-on-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal/70 mb-1.5">
                    Message
                  </label>
                  <textarea
                    required
                    rows={4}
                    className="contact-input w-full px-4 py-3 resize-none"
                    placeholder="Tell us a little about what you're looking for…"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-white rounded-full font-semibold shadow-soft hover:bg-primary-dark transition-all"
                >
                  <Send className="w-4 h-4" /> Send message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
